/**
 * URL ↔ filters для журнала "Подписание документов" (UnsignedSign).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  ВАЖНО — зачем этот модуль (для разработчиков и ИИ):                     │
 * │                                                                          │
 * │  Фильтры в URL НЕ синхронизируются автоматически — это сделано           │
 * │  СОЗНАТЕЛЬНО, чтобы избежать гонок watcher'ов и лишней логики.           │
 * │                                                                          │
 * │  URL используется только в двух точках:                                  │
 * │   1. На mount — если в URL есть фильтры (например, после возврата по    │
 * │      return_url или при ручном вводе адреса) — мы их парсим и применяем. │
 * │   2. Перед редиректом на подписание — генерируем абсолютный URL          │
 * │      текущего состояния и передаём его в API как `return_url`,           │
 * │      чтобы пользователя вернули к тем же фильтрам после подписания.      │
 * │                                                                          │
 * │  Схема ключей (per-tab, с префиксами):                                   │
 * │   tab, floor_map_document_id          — общие (управляются index.vue)    │
 * │   inv_is_signed, inv_sbs, inv_ptype,                                     │
 * │       inv_provider, inv_page          — invoices                         │
 * │   agr_is_signed, agr_sbs, agr_ptype,                                     │
 * │       agr_page                        — agreements                       │
 * │   doc_audit_tags — при наличии: объединённый фильтр подписание + ЭСФ;           │
 * │       иначе legacy: doc_is_signed, doc_has_esf; doc_resident, doc_ptype, │
 * │       doc_entrance, doc_level, doc_date_from, doc_date_to               │
 * │       — buh_docs/exec_docs (общие)                                       │
 * │   buh_types, buh_page                 — buh_docs                         │
 * │   exec_types, exec_page               — exec_docs                        │
 * │                                                                          │
 * │  ────────────────────────────────────────────────────────────────────    │
 * │  КАК ДОБАВИТЬ НОВЫЙ ФИЛЬТР:                                              │
 * │                                                                          │
 * │  1. Выбрать префикс ключа (inv_/agr_/doc_/buh_/exec_).                   │
 * │  2. Добавить парсинг ключа в parse<Tab>FromQuery (используйте хелперы    │
 * │     _str/_int/_intArray/_enum/_date/_page).                              │
 * │  3. Добавить запись ключа в build<Tab>QueryParams. Дефолтные значения    │
 * │     не пишите.                                                           │
 * │  4. В index.vue добавить поле в data() с дефолтом, в mounted — приме-    │
 * │     нить значение из parse<Tab>FromQuery в state.                        │
 * │  5. Если фильтр относится к существующему `on<Tab>Filter(key, value)` —  │
 * │     ничего больше не нужно (метод универсальный).                        │
 * │                                                                          │
 * │  Ничего не нужно делать в watcher или sync — их тут просто нет.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import {
  BUH_DOC_TYPE_IDS,
  EXEC_DOC_TYPE_IDS,
  sortFloorMapDocTypeIdsForTab,
} from "./doc-type-ids";

/** Дефолт для вкладок «Накладные» и «Договора проекта»: без фильтра по статусу подписи. */
export const DEFAULT_IS_SIGNED = "all";
/** Дефолт для вкладок «Бух / Исполнительная документация» (общий `doc.filters`): без отбора по подписи. */
export const DEFAULT_DOC_IS_SIGNED = "all";
export const DEFAULT_HAS_ESF   = "all";

/** Дефолт объединённого фильтра подписание + ЭСФ: без ограничений. */
export const DEFAULT_DOC_AUDIT_TAGS = Object.freeze(["sign_all", "esf_all"]);

const SIGN_AUDIT_TAGS = new Set(["sign_all", "sign_unsigned", "sign_signed"]);
const ESF_AUDIT_TAGS  = new Set(["esf_all", "esf_yes", "esf_no"]);

/**
 * Схлопывает мультивыбор к одному условию по подписи и одному по ЭСФ (последний выбранный в каждой группе).
 * @param {string[]} tags
 * @returns {[string, string]}
 */
export function normalizeDocAuditTags(tags) {
  const arr = Array.isArray(tags) ? tags : [];
  let sign = "sign_all";
  let esf = "esf_all";
  for (const t of arr) {
    if (SIGN_AUDIT_TAGS.has(t)) sign = t;
    if (ESF_AUDIT_TAGS.has(t)) esf = t;
  }
  return [sign, esf];
}

/** Пара строк для query API `is_signed` / `has_esf` (как раньше два отдельных селекта). */
export function docAuditTagsToApiQuery(tags) {
  const [sign, esf] = normalizeDocAuditTags(tags);
  const is_signed = sign === "sign_signed" ? "true" : sign === "sign_unsigned" ? "false" : "all";
  const has_esf = esf === "esf_yes" ? "true" : esf === "esf_no" ? "false" : "all";
  return { is_signed, has_esf };
}

function docAuditTagsUrlDefault(tags) {
  const n = normalizeDocAuditTags(tags);
  return n[0] === "sign_all" && n[1] === "esf_all";
}

/** @param {string[]} tags массив тегов (хотя бы один на ось допускается) */
function docAuditTagsPairToTagsParam(tags) {
  const [a, b] = normalizeDocAuditTags(tags);
  return `${a},${b}`;
}

/** Парсит `doc_audit_tags` из URL; иначе `null`. */
export function parseDocAuditTagsFromQuery(query) {
  const raw = _str(query, "doc_audit_tags");
  if (!raw) return null;
  const tokens = raw.split(",").map((s) => s.trim()).filter(Boolean);
  const filtered = tokens.filter((t) => SIGN_AUDIT_TAGS.has(t) || ESF_AUDIT_TAGS.has(t));
  if (filtered.length === 0) return null;
  return normalizeDocAuditTags(filtered);
}

/** Из legacy `doc_is_signed` / `doc_has_esf` в пару тегов. */
export function legacyDocEnumsToAuditTags(is_signed, has_esf) {
  const sign =
    is_signed === "true" ? "sign_signed"
    : is_signed === "false" ? "sign_unsigned"
    : "sign_all";
  const esf =
    has_esf === "true" ? "esf_yes"
    : has_esf === "false" ? "esf_no"
    : "esf_all";
  return [sign, esf];
}

export const TAB_NAMES = ["buh_docs", "exec_docs", "agreements", "invoices"];

// ── helpers ─────────────────────────────────────────────────────────────────
const _q = (query, key) => {
  const v = query?.[key];
  return Array.isArray(v) ? v[0] : v;
};
const _str = (query, key) => {
  const v = _q(query, key);
  return v == null || v === "" ? null : String(v);
};
const _int = (query, key) => {
  const s = _str(query, key);
  if (s == null) return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
};
const _intArray = (query, key) => {
  const s = _str(query, key);
  if (!s) return [];
  return s.split(",").map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
};
const _enum = (query, key, allowed, fallback) => {
  const s = _str(query, key);
  return allowed.includes(s) ? s : fallback;
};
const _date = (query, key) => {
  const s = _str(query, key);
  if (!s) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};
const _page = (query, key) => {
  const n = _int(query, key);
  return n != null && n > 0 ? n : 1;
};

const _setOrDel = (q, key, value) => {
  if (
    value == null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    delete q[key];
  } else if (Array.isArray(value)) {
    q[key] = value.join(",");
  } else {
    q[key] = String(value);
  }
};

const _sameIntSet = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const s = new Set(b);
  return a.every((x) => s.has(x));
};

// ── parsers (URL → state) ───────────────────────────────────────────────────
export function parseTabFromQuery(query) {
  const t = _str(query, "tab");
  return t && TAB_NAMES.includes(t) ? t : null;
}

export function parseInvFromQuery(query) {
  return {
    filters: {
      is_signed:       _enum(query, "inv_is_signed", ["true", "false", "all"], DEFAULT_IS_SIGNED),
      sbs_project_id:  _int(query, "inv_sbs"),
      project_type_id: _int(query, "inv_ptype"),
      provider_id:     _int(query, "inv_provider"),
    },
    page: _page(query, "inv_page"),
  };
}

export function parseAgrFromQuery(query) {
  return {
    filters: {
      is_signed:       _enum(query, "agr_is_signed", ["true", "false", "all"], DEFAULT_IS_SIGNED),
      sbs_project_id:  _int(query, "agr_sbs"),
      project_type_id: _int(query, "agr_ptype"),
    },
    page: _page(query, "agr_page"),
  };
}

/**
 * Парсит фильтры doc-таба. Общие фильтры — всегда; typeIds/page — для конкретного таба.
 * @param {object} query
 * @param {"buh_docs"|"exec_docs"} tabName — какой именно doc-таб (для typeIds/page).
 */
export function parseDocFromQuery(query, tabName) {
  let doc_audit_tags = parseDocAuditTagsFromQuery(query);
  if (!doc_audit_tags) {
    const is_signed = _enum(
      query,
      "doc_is_signed",
      ["true", "false", "all"],
      DEFAULT_DOC_IS_SIGNED,
    );
    const has_esf = _enum(
      query,
      "doc_has_esf",
      ["true", "false", "all"],
      DEFAULT_HAS_ESF,
    );
    doc_audit_tags = legacyDocEnumsToAuditTags(is_signed, has_esf);
  }
  const filters = {
    doc_audit_tags,
    resident_id:         _int(query, "doc_resident"),
    project_type_id:     _int(query, "doc_ptype"),
    project_entrance_id: _int(query, "doc_entrance"),
    level_num:           _int(query, "doc_level"),
    contractor_id:       _int(query, "doc_contractor"),
    doc_date_from:       _date(query, "doc_date_from"),
    doc_date_to:         _date(query, "doc_date_to"),
  };
  let typeIds = null;
  let page    = 1;
  if (tabName === "buh_docs") {
    const arr = _intArray(query, "buh_types");
    const raw = arr.length ? arr : BUH_DOC_TYPE_IDS.slice();
    typeIds = sortFloorMapDocTypeIdsForTab(raw, "buh_docs");
    page    = _page(query, "buh_page");
  } else if (tabName === "exec_docs") {
    const arr = _intArray(query, "exec_types");
    const raw = arr.length ? arr : EXEC_DOC_TYPE_IDS.slice();
    typeIds = sortFloorMapDocTypeIdsForTab(raw, "exec_docs");
    page    = _page(query, "exec_page");
  }
  return { filters, typeIds, page };
}

// ── builders (state → URL params) ───────────────────────────────────────────
export function buildInvQueryParams({ filters, page }) {
  const q = {};
  _setOrDel(q, "inv_is_signed", filters.is_signed === DEFAULT_IS_SIGNED ? null : filters.is_signed);
  _setOrDel(q, "inv_sbs",       filters.sbs_project_id);
  _setOrDel(q, "inv_ptype",     filters.project_type_id);
  _setOrDel(q, "inv_provider",  filters.provider_id);
  _setOrDel(q, "inv_page",      page > 1 ? page : null);
  return q;
}

export function buildAgrQueryParams({ filters, page }) {
  const q = {};
  _setOrDel(q, "agr_is_signed", filters.is_signed === DEFAULT_IS_SIGNED ? null : filters.is_signed);
  _setOrDel(q, "agr_sbs",       filters.sbs_project_id);
  _setOrDel(q, "agr_ptype",     filters.project_type_id);
  _setOrDel(q, "agr_page",      page > 1 ? page : null);
  return q;
}

/**
 * @param {"buh_docs"|"exec_docs"} tabName
 * @param {object} state.filters    — общие фильтры doc
 * @param {number[]} state.typeIds  — typeIds для tabName
 * @param {number} state.page       — страница для tabName
 */
export function buildDocQueryParams(tabName, { filters, typeIds, page }) {
  const q = {};
  if (!docAuditTagsUrlDefault(filters.doc_audit_tags)) {
    _setOrDel(q, "doc_audit_tags", docAuditTagsPairToTagsParam(filters.doc_audit_tags));
  }
  _setOrDel(q, "doc_resident",   filters.resident_id);
  _setOrDel(q, "doc_ptype",      filters.project_type_id);
  _setOrDel(q, "doc_entrance",   filters.project_entrance_id);
  _setOrDel(q, "doc_level",      filters.level_num);
  _setOrDel(q, "doc_contractor", filters.contractor_id);
  _setOrDel(q, "doc_date_from",  filters.doc_date_from);
  _setOrDel(q, "doc_date_to",    filters.doc_date_to);
  if (tabName === "buh_docs") {
    _setOrDel(q, "buh_types", _sameIntSet(typeIds, BUH_DOC_TYPE_IDS) ? null : typeIds);
    _setOrDel(q, "buh_page",  page > 1 ? page : null);
  } else if (tabName === "exec_docs") {
    _setOrDel(q, "exec_types", _sameIntSet(typeIds, EXEC_DOC_TYPE_IDS) ? null : typeIds);
    _setOrDel(q, "exec_page",  page > 1 ? page : null);
  }
  return q;
}

/**
 * Собирает абсолютный URL текущего таба со всеми его фильтрами.
 * Используется для передачи в API подписания как `return_url` —
 * чтобы пользователя вернули к тем же фильтрам после подписания.
 *
 * @param {string} pathname — путь страницы (например, "/unsigned-sign")
 * @param {string} tabName — активный таб
 * @param {object} extras — дополнительные ключи (floor_map_document_id и т.п.)
 * @param {object} tabState — состояние активного таба
 *   - для invoices/agreements: { filters, page }
 *   - для buh_docs/exec_docs:  { filters, typeIds, page }
 * @returns {string} абсолютный URL
 */
export function buildReturnUrl(pathname, tabName, extras, tabState) {
  let params = { tab: tabName };
  if (tabName === "invoices") {
    params = { ...params, ...buildInvQueryParams(tabState) };
  } else if (tabName === "agreements") {
    params = { ...params, ...buildAgrQueryParams(tabState) };
  } else if (tabName === "buh_docs" || tabName === "exec_docs") {
    params = { ...params, ...buildDocQueryParams(tabName, tabState) };
  }
  if (extras) {
    for (const [k, v] of Object.entries(extras)) {
      if (v != null && v !== "") params[k] = String(v);
    }
  }
  const usp = new URLSearchParams(params);
  const qs = usp.toString();
  const origin = (typeof window !== "undefined" && window.location) ? window.location.origin : "";
  return origin + pathname + (qs ? `?${qs}` : "");
}
